/**
 * Job Event Card Component
 * 
 * Specialized event card for displaying job information on calendar
 * Shows: Postcode, Order Number, Customer Name, Email, Phone
 * Expandable to show additional details
 */

import React from 'react';
import { ChevronDown, Mail, Phone, Truck, Package, Clock, Lock, Calendar, Factory, ShoppingBag, FileText, Clipboard, StickyNote, ChevronUp, ChevronDown as ChevronDownIcon } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import type { JobEventCardProps, JobEventNotes } from '../../types/calendar';
import { formatCurrency } from '../../utils/calendarHelpers';
import { isJobLocked, formatLockTooltip } from '../../utils/lockHelpers';
import { getUserEventColor } from '../../utils/colorHelpers';
import {
  getJobStatusBadge,
  getAdminJobStatusLabel,
  getAdminJobStatusPillClassNames,
  getStatusBorderAndOpacity,
} from '../../utils/jobStatusMapper';

export const JobEventCard: React.FC<JobEventCardProps> = ({
  event,
  compact = false,
  onClick,
  isExpanded = false,
  onToggleExpand,
  view = 'month',
  onMoveUp,
  onMoveDown,
  canMoveUp = false,
  canMoveDown = false,
}) => {
  const notes = event.notes as JobEventNotes | undefined;
  const {
    postcode,
    orderNumber,
    customerName,
    customerEmail,
    customerPhone,
    fulfillmentType,
    totalAmount,
    createdAt,
    updatedAt,
    deliveryNotes,
    deliveryAddressLine1,
    deliveryAddressLine2,
    glassArrivalDate,
    manufactureDate,
    totalItems,
    productionNotes,
    adminNotes,
    deliveryUserData,
  } = notes || {};

  // Get dynamic colors based on delivery user
  const colors = getUserEventColor(deliveryUserData);

  const jobForStatus = notes?.job;
  const currentStatus = getJobStatusBadge({
    status: jobForStatus?.status,
    production_status: jobForStatus?.production_status,
    fulfillment_type: jobForStatus?.fulfillment_type ?? fulfillmentType,
    delivery_result: jobForStatus?.delivery_result,
    collection_completed: jobForStatus?.collection_completed,
  });
  const statusStyle = getStatusBorderAndOpacity(currentStatus);
  const adminStatusLabel = jobForStatus
    ? getAdminJobStatusLabel({
        status: jobForStatus.status,
        production_status: jobForStatus.production_status,
        fulfillment_type: jobForStatus.fulfillment_type ?? fulfillmentType,
        delivery_result: jobForStatus.delivery_result,
        collection_completed: jobForStatus.collection_completed,
      })
    : null;
  const adminStatusPillClass = jobForStatus
    ? getAdminJobStatusPillClassNames({
        status: jobForStatus.status,
        production_status: jobForStatus.production_status,
        fulfillment_type: jobForStatus.fulfillment_type ?? fulfillmentType,
        delivery_result: jobForStatus.delivery_result,
        collection_completed: jobForStatus.collection_completed,
      })
    : '';

  // Helper function to extract plain text from admin notes (removes timestamps and usernames)
  const extractPlainNotes = (notesText: string | null | undefined): string => {
    if (!notesText) return '';
    
    // Split by newlines and process each line
    const lines = notesText.split('\n').filter(line => line.trim());
    const plainLines = lines.map(line => {
      // Remove format: [timestamp] username: text -> text
      const match = line.match(/^\[.*?\]\s+[^:]+:\s*(.*)$/);
      return match ? match[1] : line;
    });
    
    return plainLines.join('\n');
  };

  // Lock status
  const lockData = notes?.job?.lock_data;
  const showLockIcon = isJobLocked(lockData);

  // Handle expand button click
  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent modal from opening
    if (onToggleExpand && event.id) {
      onToggleExpand(event.id);
    }
  };

  // Handle quick action clicks
  const handleCallClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (customerPhone) {
      window.location.href = `tel:${customerPhone}`;
    }
  };

  const handleEmailClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (customerEmail) {
      window.location.href = `mailto:${customerEmail}`;
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (notes?.jobId) {
      window.location.href = `/jobs/${notes.jobId}/edit`;
    }
  };

  // Handle reorder button clicks
  const handleMoveUp = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMoveUp && event.id) {
      onMoveUp(event.id);
    }
  };

  const handleMoveDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMoveDown && event.id) {
      onMoveDown(event.id);
    }
  };

  // Show reorder buttons only in week/day view
  const showReorderButtons = (view === 'week' || view === 'day') && (canMoveUp || canMoveDown);

  // Render card content
  return (
    <div
      className={`rounded shadow-sm hover:shadow-md cursor-move relative flex flex-col ${
        (view === 'week' || view === 'day') ? 'px-1 py-0.5 mb-0' : 'px-1.5 py-0.5 mb-0.5'
      }`}
      title="Drag to reschedule delivery"
      style={{ 
        height: '100%', 
        minHeight: (view === 'week' || view === 'day') ? '40px' : '85px', 
        maxHeight: '100%',
        backgroundColor: colors.bg,
        color: colors.text,
        borderColor: statusStyle.borderColor,
        borderWidth: statusStyle.borderWidth,
        borderStyle: 'solid',
        opacity: statusStyle.opacity,
      }}
    >
      {/* Status Badge (Top-left corner) */}
      <div 
        className="absolute top-1 left-1 z-10 px-1.5 py-0.5 rounded text-[10px] font-bold text-white"
        style={{ 
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
        }}
        title={`Status: ${currentStatus}`}
      >
        {currentStatus}
      </div>

      {/* Lock Icon */}
      {showLockIcon && (
        <div 
          className="absolute top-1 right-1 z-10"
          title={formatLockTooltip(lockData)}
        >
          <Lock 
            className="w-5 h-5 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
            strokeWidth={2.5}
          />
        </div>
      )}

      {/* Reorder Buttons (Week/Day view only) */}
      {showReorderButtons && (
        <div className="absolute top-8 left-1 z-10 flex flex-col gap-0.5">
          {canMoveUp && (
            <button
              onClick={handleMoveUp}
              className="bg-white/90 hover:bg-white rounded p-0.5 shadow-md transition-colors"
              title="Move up in order"
              type="button"
            >
              <ChevronUp className="w-4 h-4 text-gray-700" strokeWidth={2.5} />
            </button>
          )}
          {canMoveDown && (
            <button
              onClick={handleMoveDown}
              className="bg-white/90 hover:bg-white rounded p-0.5 shadow-md transition-colors"
              title="Move down in order"
              type="button"
            >
              <ChevronDownIcon className="w-4 h-4 text-gray-700" strokeWidth={2.5} />
            </button>
          )}
        </div>
      )}

      {/* Header Section (always visible) */}
      <div className={`card-header flex-shrink-0 ${(view === 'week' || view === 'day') ? 'flex items-center justify-center flex-1' : ''}`}>
        {(view === 'week' || view === 'day') ? (
          // Week/Day view: Single line layout for minimal height
          <div className="font-semibold text-xs leading-none truncate text-center">
            {postcode} - #{orderNumber} - {customerName}
          </div>
        ) : (
          // Month view: Multi-line layout for readability
          <>
            {/* Postcode - Bold and prominent */}
            <div className="font-bold text-base leading-tight mb-0.5">{postcode}</div>
            
            {/* Order Number */}
            <div className="text-sm opacity-95 leading-tight mb-0.5">#{orderNumber}</div>
            
            {/* Customer Name */}
            <div className="text-sm opacity-90 leading-tight truncate">{customerName}</div>
          </>
        )}
      </div>

      {/* Expand/Collapse Button */}
      <button
        onClick={handleExpandClick}
        className={`w-full flex items-center justify-center gap-0.5 font-medium opacity-90 hover:opacity-100 transition-opacity flex-shrink-0 ${
          (view === 'week' || view === 'day') ? 'py-2 text-[12px] mt-0 border-t-0 leading-none' : 'py-0.5 text-xs mt-auto border-t border-white/30'
        }`}
        type="button"
      >
        <ChevronDown
          className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''} ${
            (view === 'week' || view === 'day') ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5'
          }`}
        />
        <span className={(view === 'week' || view === 'day') ? 'leading-none' : ''}>{isExpanded ? 'Less' : 'More'}</span>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="expanded-content mt-1.5 pt-1.5 border-t border-white/30 space-y-1.5 animate-slideDown">
          {/* Contact Information */}
          <div className="space-y-1">
            {customerEmail && (
              <a
                href={`mailto:${customerEmail}`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1.5 text-xs hover:underline"
              >
                <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{customerEmail}</span>
              </a>
            )}
            {customerPhone && (
              <a
                href={`tel:${customerPhone}`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1.5 text-xs hover:underline"
              >
                <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{customerPhone}</span>
              </a>
            )}
          </div>

          {/* Job Details */}
          <div className="space-y-1 text-xs">
            {totalAmount !== undefined && (
              <div className="flex items-center gap-1.5">
                <span className="opacity-90">Amount:</span>
                <span className="font-semibold">{formatCurrency(totalAmount)}</span>
              </div>
            )}
            
            {adminStatusLabel && (
              <div className="flex items-center gap-1.5">
                <span className="opacity-90">Status:</span>
                <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium border ${adminStatusPillClass}`}>
                  {adminStatusLabel}
                </span>
              </div>
            )}

            {fulfillmentType && (
              <div className="flex items-center gap-1.5">
                {fulfillmentType === 'delivery' ? (
                  <Truck className="w-3.5 h-3.5" />
                ) : (
                  <Package className="w-3.5 h-3.5" />
                )}
                <span className="capitalize">{fulfillmentType}</span>
              </div>
            )}
          </div>

          {/* Address */}
          {(deliveryAddressLine1 || deliveryAddressLine2) && (
            <div className="text-xs space-y-0.5 opacity-95">
              {deliveryAddressLine1 && <div>{deliveryAddressLine1}</div>}
              {deliveryAddressLine2 && <div>{deliveryAddressLine2}</div>}
            </div>
          )}

          {/* Delivery Notes */}
          {deliveryNotes && (
            <div className="text-xs opacity-95 bg-white/10 rounded p-2">
              <div className="flex items-start gap-1.5">
                <FileText className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium mb-0.5">Delivery Notes:</div>
                  <div className="opacity-90 whitespace-pre-line">{deliveryNotes}</div>
                </div>
              </div>
            </div>
          )}

          {/* Production Dates */}
          {(glassArrivalDate || manufactureDate) && (
            <div className="text-xs space-y-1 bg-white/10 rounded p-2">
              {glassArrivalDate && (
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="font-medium">Glass Arrival:</span>
                  <span className="opacity-90">{format(new Date(glassArrivalDate), 'dd MMM yyyy')}</span>
                </div>
              )}
              {manufactureDate && (
                <div className="flex items-center gap-1.5">
                  <Factory className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="font-medium">Manufacture:</span>
                  <span className="opacity-90">{format(new Date(manufactureDate), 'dd MMM yyyy')}</span>
                </div>
              )}
            </div>
          )}

          {/* Total Items */}
          {totalItems !== undefined && totalItems > 0 && (
            <div className="text-xs bg-white/10 rounded p-2">
              <div className="flex items-center gap-1.5">
                <ShoppingBag className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="font-medium">Total Items:</span>
                <span className="opacity-90">{totalItems}</span>
              </div>
            </div>
          )}

          {/* Production Notes */}
          {productionNotes && extractPlainNotes(productionNotes) && (
            <div className="text-xs opacity-95 bg-white/10 rounded p-2">
              <div className="flex items-start gap-1.5">
                <Clipboard className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium mb-0.5">Production Notes:</div>
                  <div className="opacity-90 whitespace-pre-line">{extractPlainNotes(productionNotes)}</div>
                </div>
              </div>
            </div>
          )}

          {/* Admin Notes */}
          {adminNotes && extractPlainNotes(adminNotes) && (
            <div className="text-xs opacity-95 bg-white/10 rounded p-2">
              <div className="flex items-start gap-1.5">
                <StickyNote className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium mb-0.5">Admin Notes:</div>
                  <div className="opacity-90 whitespace-pre-line">{extractPlainNotes(adminNotes)}</div>
                </div>
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="text-[11px] opacity-80 space-y-0.5">
            {createdAt && (
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>Created {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}</span>
              </div>
            )}
            {updatedAt && (
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>Updated {formatDistanceToNow(new Date(updatedAt), { addSuffix: true })}</span>
              </div>
            )}
          </div>

          {/* Quick Action Buttons */}
          <div className="flex gap-1.5 pt-1.5 border-t border-white/30">
            {customerPhone && (
              <button
                onClick={handleCallClick}
                className="flex-1 px-2 py-1.5 bg-white/20 hover:bg-white/30 rounded text-xs font-medium transition-colors"
                type="button"
              >
                Call
              </button>
            )}
            {customerEmail && (
              <button
                onClick={handleEmailClick}
                className="flex-1 px-2 py-1.5 bg-white/20 hover:bg-white/30 rounded text-xs font-medium transition-colors"
                type="button"
              >
                Email
              </button>
            )}
            <button
              onClick={handleEditClick}
              className="flex-1 px-2 py-1.5 bg-white/20 hover:bg-white/30 rounded text-xs font-medium transition-colors"
              type="button"
            >
              Edit
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

